/**
 * BroadcastersPage: admin CRUD for broadcaster profiles (EPIC-108,
 * PMA-BCAST-005). Route /admin/broadcasters (admin-only via ProtectedRoute
 * in App.tsx). Logos are SVG-only, like team logos.
 */
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  createBroadcaster,
  deleteBroadcaster,
  listBroadcasters,
  updateBroadcaster,
  uploadBroadcasterLogo,
  type Broadcaster,
} from "../../lib/broadcastApi";
import LoadingSpinner from "../../components/LoadingSpinner";

const errorText = (err: unknown, fallback: string): string => {
  const detail = (err as { response?: { data?: { detail?: unknown } } })
    ?.response?.data?.detail;
  return typeof detail === "string" && detail ? detail : fallback;
};

const HEX = /^#[0-9a-fA-F]{6}$/;

export default function BroadcastersPage() {
  const { t } = useTranslation("broadcast");
  const [items, setItems] = useState<Broadcaster[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [name, setName] = useState("");
  const [primary, setPrimary] = useState("#0ea5e9");
  const [secondary, setSecondary] = useState("#1e293b");
  const [editing, setEditing] = useState<Broadcaster | null>(null);

  const load = useCallback(async () => {
    try {
      setItems(await listBroadcasters());
      setError("");
    } catch (err) {
      setError(errorText(err, t("broadcasters.loadFailed")));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const run = async (fn: () => Promise<void>, failure: string) => {
    setBusy(true);
    try {
      await fn();
      await load();
    } catch (err) {
      setError(errorText(err, failure));
    } finally {
      setBusy(false);
    }
  };

  const create = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !HEX.test(primary) || !HEX.test(secondary)) return;
    void run(async () => {
      await createBroadcaster({
        name: name.trim(),
        primary_color: primary,
        secondary_color: secondary,
      });
      setName("");
    }, t("broadcasters.saveFailed"));
  };

  const saveEdit = () => {
    if (!editing) return;
    const e = editing;
    void run(async () => {
      await updateBroadcaster(e.broadcaster_id, {
        name: e.name.trim(),
        primary_color: e.primary_color,
        secondary_color: e.secondary_color,
      });
      setEditing(null);
    }, t("broadcasters.saveFailed"));
  };

  const remove = (b: Broadcaster) => {
    if (!window.confirm(t("broadcasters.confirmDelete", { name: b.name })))
      return;
    void run(async () => {
      await deleteBroadcaster(b.broadcaster_id);
    }, t("broadcasters.deleteFailed"));
  };

  const upload = (b: Broadcaster, file: File | undefined) => {
    if (!file) return;
    void run(async () => {
      await uploadBroadcasterLogo(b.broadcaster_id, file);
    }, t("broadcasters.logoFailed"));
  };

  const field = "rounded-md border border-gray-300 px-2 py-1 text-sm";

  return (
    <div className="min-h-screen bg-gray-50" data-testid="broadcasters-page">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-5xl mx-auto px-4 py-6">
          <h1 className="text-2xl font-bold text-gray-900">
            {t("broadcasters.title")}
          </h1>
          <p className="mt-1 text-sm text-gray-600">
            {t("broadcasters.subtitle")}
          </p>
        </div>
      </header>
      <main className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        {error && (
          <p
            role="alert"
            data-testid="broadcasters-error"
            className="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-800"
          >
            {error}
          </p>
        )}

        <form
          onSubmit={create}
          className="rounded-lg border border-gray-200 bg-white p-4 flex flex-wrap items-end gap-3"
          data-testid="broadcaster-create-form"
        >
          <div>
            <label
              className="block text-xs font-medium text-gray-700"
              htmlFor="bc-name"
            >
              {t("broadcasters.name")}
            </label>
            <input
              id="bc-name"
              data-testid="broadcaster-name-input"
              className={field}
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={80}
            />
          </div>
          <div>
            <label
              className="block text-xs font-medium text-gray-700"
              htmlFor="bc-primary"
            >
              {t("broadcasters.primaryColor")}
            </label>
            <input
              id="bc-primary"
              type="color"
              value={primary}
              onChange={(e) => setPrimary(e.target.value)}
              className="h-8 w-12"
            />
          </div>
          <div>
            <label
              className="block text-xs font-medium text-gray-700"
              htmlFor="bc-secondary"
            >
              {t("broadcasters.secondaryColor")}
            </label>
            <input
              id="bc-secondary"
              type="color"
              value={secondary}
              onChange={(e) => setSecondary(e.target.value)}
              className="h-8 w-12"
            />
          </div>
          <button
            type="submit"
            disabled={busy || !name.trim()}
            data-testid="broadcaster-create-submit"
            className="rounded-md bg-primary-600 px-4 py-1.5 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-50"
          >
            {t("broadcasters.create")}
          </button>
        </form>

        {loading ? (
          <LoadingSpinner size="md" message={t("broadcasters.loading")} />
        ) : items.length === 0 ? (
          <p data-testid="broadcasters-empty" className="text-sm text-gray-500">
            {t("broadcasters.empty")}
          </p>
        ) : (
          <ul className="space-y-3" data-testid="broadcasters-list">
            {items.map((b) => {
              const isEditing = editing?.broadcaster_id === b.broadcaster_id;
              return (
                <li
                  key={b.broadcaster_id}
                  data-testid={`broadcaster-row-${b.broadcaster_id}`}
                  className="rounded-lg border border-gray-200 bg-white p-4 flex flex-wrap items-center gap-4"
                >
                  <div className="h-12 w-12 rounded border border-gray-200 flex items-center justify-center overflow-hidden bg-gray-50">
                    {b.logo_url ? (
                      <img
                        src={b.logo_url}
                        alt={b.name}
                        className="max-h-full max-w-full"
                      />
                    ) : (
                      <span className="text-xs text-gray-400">
                        {t("broadcasters.noLogo")}
                      </span>
                    )}
                  </div>
                  {isEditing && editing ? (
                    <div className="flex flex-wrap items-center gap-2">
                      <input
                        aria-label={t("broadcasters.name")}
                        className={field}
                        value={editing.name}
                        onChange={(e) =>
                          setEditing({ ...editing, name: e.target.value })
                        }
                      />
                      <input
                        type="color"
                        aria-label={t("broadcasters.primaryColor")}
                        value={editing.primary_color}
                        onChange={(e) =>
                          setEditing({
                            ...editing,
                            primary_color: e.target.value,
                          })
                        }
                        className="h-8 w-12"
                      />
                      <input
                        type="color"
                        aria-label={t("broadcasters.secondaryColor")}
                        value={editing.secondary_color}
                        onChange={(e) =>
                          setEditing({
                            ...editing,
                            secondary_color: e.target.value,
                          })
                        }
                        className="h-8 w-12"
                      />
                      <button
                        type="button"
                        disabled={busy}
                        onClick={saveEdit}
                        data-testid={`broadcaster-save-${b.broadcaster_id}`}
                        className="rounded-md bg-primary-600 px-3 py-1 text-xs font-semibold text-white"
                      >
                        {t("broadcasters.save")}
                      </button>
                      <button
                        type="button"
                        onClick={() => setEditing(null)}
                        className="rounded-md border border-gray-300 px-3 py-1 text-xs"
                      >
                        {t("panel.cancel")}
                      </button>
                    </div>
                  ) : (
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-gray-900">
                        {b.name}
                      </div>
                      <div className="mt-1 flex items-center gap-2 text-xs text-gray-600">
                        <span
                          className="inline-block h-4 w-4 rounded border border-gray-300"
                          style={{ background: b.primary_color }}
                        />
                        <span
                          className="inline-block h-4 w-4 rounded border border-gray-300"
                          style={{ background: b.secondary_color }}
                        />
                        {b.primary_color} / {b.secondary_color}
                      </div>
                    </div>
                  )}
                  <div className="flex flex-wrap items-center gap-2 text-xs">
                    <label className="cursor-pointer rounded-md border border-gray-300 px-3 py-1 hover:bg-gray-50">
                      {t("broadcasters.uploadLogo")}
                      <input
                        type="file"
                        accept=".svg,image/svg+xml"
                        className="sr-only"
                        data-testid={`broadcaster-logo-input-${b.broadcaster_id}`}
                        onChange={(e) => {
                          upload(b, e.target.files?.[0]);
                          e.target.value = "";
                        }}
                      />
                    </label>
                    {!isEditing && (
                      <button
                        type="button"
                        onClick={() => setEditing(b)}
                        data-testid={`broadcaster-edit-${b.broadcaster_id}`}
                        className="rounded-md border border-gray-300 px-3 py-1 hover:bg-gray-50"
                      >
                        {t("broadcasters.edit")}
                      </button>
                    )}
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => remove(b)}
                      data-testid={`broadcaster-delete-${b.broadcaster_id}`}
                      className="rounded-md border border-red-300 px-3 py-1 text-red-700 hover:bg-red-50"
                    >
                      {t("broadcasters.delete")}
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </main>
    </div>
  );
}
